import * as React from "react";
import { graphql } from "gatsby";
import { Trans, useI18next, useTranslation } from "gatsby-plugin-react-i18next";
import PageLayout from "../components/page-layout/PageLayout";
import SEO from "../components/SEO";
import JobPositions from "../components/sections/JobPositions";
import { JoinHeroCta } from "../components/cta/JoinHeroCta";
import ImageHero from "../components/hero/ImageHero";
import TextSection from "../components/text/TextSection";
import { Process } from "../components/Process";

// @ts-ignore
import joinUsImage from "../assets/images/join.jpg";
import { ImageFeaturesGrid4 } from "../components/features-grid/ImageFeaturesGrid4";
import { ImageFeaturesGrid } from "../components/features-grid/ImageFeaturesGrid";
import ImageCta from "../components/cta/ImageCta";
import { Button } from "../components/Button";

const sectionsToHtml = (
  sections: Array<
    {
      title: {
        en: string;
        it: string;
      };
    } & (
      | {
          type: "text";
          text: {
            en: string;
            it: string;
          };
        }
      | {
          type: "list";
          list: {
            en: string[];
            it: string[];
          };
        }
    )
  >,
  lang: string,
) =>
  sections
    .map((section) => {
      if (section.type === "text") {
        return `${section.title[lang]}<br/>${section.text[lang]}`;
      } else if (section.type === "list") {
        const list = section.list[lang]
          .map((item) => `<li>${item}</li>`)
          .join("");
        return `${section.title[lang]}<br/><ul>${list}</ul>`;
      }
    })
    .join("<br/><br/>");

const JoinPage = ({
  data,
  location,
}: {
  location: { hash: string };
  data: {
    contactMail: { url: string };
    jobPositions: {
      nodes: {
        salary: [number, number];
        seniority: string;
        name: {
          en: string;
          it: string;
        };
        sections: any[];
      }[];
    };
  };
}) => {
  const { t } = useTranslation();
  const { language } = useI18next();

  const values: Array<{
    name: string;
    description: string;
    icon: string;
  }> = t("join:values.list", { returnObjects: true });

  const benefits: Array<{
    name: string;
    description: string;
  }> = t("join:benefits.list", { returnObjects: true });

  const recruitingSteps: Array<{
    title: string;
    description: string;
  }> = t("join:process.steps", { returnObjects: true });

  return (
    <PageLayout pageName="join" headerBg="gray-100" contactForm={false}>
      <SEO
        titleI18nKey={"join:meta.title"}
        descriptionI18nKey={"join:meta.description"}
        image={joinUsImage}
        datePublished={new Date("2021-03-01T08:00:00Z")}
        dateModified={new Date("2022-03-25T08:00:00Z")}
        jobPositions={data.jobPositions.nodes.map((job) => ({
          title: job.name[language],
          description: sectionsToHtml(job.sections, language),
          salary: job.salary,
          seniority: job.seniority,
        }))}
      />

      <ImageHero
        tag={t("join:hero.tag")}
        title={
          <Trans
            i18nKey="join:hero.title"
            components={{ ol: <span className="text-ol-gradient" /> }}
          />
        }
        description={t("join:hero.content")}
        image={joinUsImage}
        imgPosition="object-left"
        cta={<JoinHeroCta />}
      />

      {/* WHAT WILL YOU FIND */}
      <TextSection
        tag={t("join:whatWillYouFind.tag")}
        title={t("join:whatWillYouFind.title")}
        text={<Trans i18nKey="join:whatWillYouFind.description" />}
      >
        <JoinHeroCta text={t("join:whatWillYouFind.cta")} />
      </TextSection>

      {/* VALUES */}
      <ImageFeaturesGrid
        tag={t("join:values.tag")}
        title={t("join:values.title")}
        elements={values}
        theme="gray"
        image={require("../assets/images/lavora-con-noi-astrorei.jpg").default}
      />

      {/* BENEFITS */}
      <ImageFeaturesGrid4
        image={
          require("../assets/images/astrorei-team-sviluppatori-software.jpg")
            .default
        }
        tag={t("join:benefits.tag")}
        title={
          <Trans
            i18nKey="join:benefits.title"
            components={{ ol: <span className="text-ol-gradient" /> }}
          />
        }
        elements={benefits}
      />

      {/* RECRUITING PROCESS */}
      <Process
        theme="gray"
        tag={t("join:process.tag")}
        title={t("join:process.title")}
        steps={recruitingSteps}
      />

      <JobPositions defaultOpen={location.hash.replace("#", "")} />

      <ImageCta
        tag={t(`join:network.tag`)}
        title={t(`join:network.title`)}
        description={t(`join:network.description`)}
        image={require("../assets/images/astrorei-cta.jpg").default}
        cta={
          <Button
            to={data.contactMail.url}
            label={t("join:network.cta")}
            variant="secondary"
          />
        }
      />
    </PageLayout>
  );
};

export default JoinPage;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    contactMail: contactsJson(jsonId: { eq: "info" }) {
      url
    }
    jobPositions: allJobPositionsJson(filter: { enabled: { eq: true } }) {
      nodes {
        salary
        seniority
        sections {
          list {
            en
            it
          }
          text {
            en
            it
          }
          title {
            en
            it
          }
          type
        }
        name {
          en
          it
        }
      }
    }
  }
`;
